<script setup lang="ts">
  import { computed, useTemplateRef } from 'vue';
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import FontIcon from '../../FontIcon/FontIcon.vue';
  import TextInput from '../TextInput/TextInput.vue';
  import { clearButtonClass, leadingIconClass } from './SearchInput.css';
  import type { SearchInputEmits, SearchInputProps } from './SearchInput.types';
  import {
    filterSearchInput,
    SEARCH_INPUT_PATTERN,
  } from './search-input.utils';

  const props = withDefaults(defineProps<SearchInputProps>(), {
    modelValue: '',
    clearable: false,
    disabled: false,
    autofocus: false,
  });

  const emit = defineEmits<SearchInputEmits>();

  const { t } = useLocale('ds-components');

  const resolvedPlaceholder = computed(
    () => props.placeholder ?? t('search_input.placeholder'),
  );

  const showClear = computed(
    () => props.clearable && !!props.modelValue && !props.disabled,
  );

  const textInputRef =
    useTemplateRef<InstanceType<typeof TextInput>>('textInputRef');

  // The filtered value can equal the current modelValue (the user typed a
  // disallowed character), so the prop never changes and Vue skips the DOM
  // patch. Resync the element explicitly to drop the rejected character.
  const onUpdate = (value: string) => {
    const filtered = filterSearchInput(value);
    const inputEl = textInputRef.value?.inputRef;

    if (inputEl && inputEl.value !== filtered) {
      inputEl.value = filtered;
    }

    emit('update:modelValue', filtered);
  };

  const onClear = () => {
    emit('update:modelValue', '');
  };
</script>

<template>
  <TextInput
    ref="textInputRef"
    type="text"
    :name="name"
    :test-id="testId"
    :model-value="modelValue"
    :placeholder="resolvedPlaceholder"
    :pattern="SEARCH_INPUT_PATTERN"
    :aria-label="ariaLabel ?? resolvedPlaceholder"
    :disabled="disabled"
    :autofocus="autofocus"
    :size="size"
    @update:model-value="onUpdate"
    @blur="(event) => emit('blur', event)"
  >
    <template #leadingIcon>
      <slot name="leading">
        <FontIcon :icon-name="TUNDR_ICONS.SEARCH" :class="leadingIconClass" />
      </slot>
    </template>
    <template #icon v-if="showClear">
      <button
        type="button"
        :class="clearButtonClass"
        :aria-label="t('search_input.clear_aria_label')"
        @click="onClear"
      >
        <FontIcon :icon-name="TUNDR_ICONS.CLOSE" />
      </button>
    </template>
  </TextInput>
</template>
